<template>
  <div class="my-account-security">
    <b-card class="mb-0">
      <b-overlay
        :show="loading || verifying"
        rounded="sm"
      >
        <div class="my-account-security__panel">
          <div class="my-account-security__header">
            <div class="my-account-security__title">
              <feather-icon
                icon="ShieldIcon"
                size="18"
                class="mr-50 text-primary"
              />
              <h5 class="mb-0">
                {{ $t('myAccount.security.title') }}
              </h5>
            </div>
            <span class="my-account-security__status">
              {{ challengeId ? (isChallengeExpired ? $t('myAccount.security.setupExpired') : $t('myAccount.security.setupInProgress')) : $t('myAccount.security.ready') }}
            </span>
          </div>

          <div class="my-account-security__grid">
            <section class="my-account-security__section">
              <div class="my-account-security__section-header">
                <span class="my-account-security__step">1</span>
                <div>
                  <h6>{{ $t('myAccount.security.confirmCurrentTitle') }}</h6>
                  <p>{{ $t('myAccount.security.confirmCurrentDescription') }}</p>
                </div>
              </div>

              <b-form-group
                :label="$t('myAccount.security.currentCode')"
                label-for="totp-current-code-0"
              >
                <div
                  class="my-account-security__otp-inputs"
                  dir="ltr"
                >
                  <input
                    v-for="(_, index) in currentDigits"
                    :id="index === 0 ? 'totp-current-code-0' : null"
                    :key="`current-${index}`"
                    ref="currentDigit"
                    :value="currentDigits[index]"
                    class="my-account-security__otp-input form-control"
                    inputmode="numeric"
                    maxlength="1"
                    autocomplete="one-time-code"
                    :disabled="loading || verifying || !!challengeId || replayCountdownSeconds > 0"
                    :aria-label="`${$t('myAccount.security.currentCode')} ${index + 1}`"
                    @input="onDigitInput('current', $event, index)"
                    @keydown="onDigitKeydown('current', $event, index)"
                    @paste.prevent="onDigitPaste('current', $event, index)"
                  />
                </div>
                <small
                  v-if="currentInlineMessage"
                  class="my-account-security__countdown"
                >
                  {{ currentInlineMessage }}
                </small>
              </b-form-group>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                :disabled="loading || verifying || !isCurrentCodeComplete || !!challengeId || replayCountdownSeconds > 0"
                @click="beginChange"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="14"
                  class="mr-50"
                />
                <span v-if="loading">{{ $t('myAccount.security.starting') }}</span>
                <span v-else>{{ $t('myAccount.security.startChange') }}</span>
              </b-button>
            </section>

            <section
              class="my-account-security__section"
              :class="{ 'my-account-security__section--muted': !challengeId }"
            >
              <div class="my-account-security__section-header">
                <span class="my-account-security__step">2</span>
                <div>
                  <h6>{{ $t('myAccount.security.setupNewTitle') }}</h6>
                  <p>{{ $t('myAccount.security.setupNewDescription') }}</p>
                </div>
              </div>

              <template v-if="challengeId">
                <div class="my-account-security__setup">
                  <div
                    v-if="qrDataUri"
                    class="my-account-security__qr"
                  >
                    <img
                      :src="qrDataUri"
                      :alt="$t('myAccount.security.qrAlt')"
                    >
                  </div>

                  <div class="my-account-security__setup-details">
                    <small>{{ $t('myAccount.security.setupKey') }}</small>
                    <code>{{ formattedManualKey }}</code>
                    <div class="my-account-security__setup-actions">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        :disabled="!manualKey"
                        @click="copyManualKey"
                      >
                        <feather-icon
                          icon="CopyIcon"
                          size="13"
                          class="mr-50"
                        />
                        {{ $t('myAccount.security.copyKey') }}
                      </b-button>
                      <b-button
                        v-if="otpauthUri"
                        variant="outline-secondary"
                        size="sm"
                        :href="otpauthUri"
                      >
                        <feather-icon
                          icon="ExternalLinkIcon"
                          size="13"
                          class="mr-50"
                        />
                        {{ $t('myAccount.security.openApp') }}
                      </b-button>
                    </div>
                    <small
                      v-if="challengeCountdownMessage"
                      class="my-account-security__challenge-countdown"
                      :class="{ 'my-account-security__challenge-countdown--expired': isChallengeExpired }"
                    >
                      {{ challengeCountdownMessage }}
                    </small>
                  </div>
                </div>

                <b-form-group
                  :label="$t('myAccount.security.newCode')"
                  label-for="totp-new-code-0"
                >
                  <div
                    class="my-account-security__otp-inputs"
                    dir="ltr"
                  >
                    <input
                      v-for="(_, index) in newDigits"
                      :id="index === 0 ? 'totp-new-code-0' : null"
                      :key="`new-${index}`"
                      ref="newDigit"
                      :value="newDigits[index]"
                      class="my-account-security__otp-input form-control"
                      inputmode="numeric"
                      maxlength="1"
                      autocomplete="one-time-code"
                      :disabled="loading || verifying || replayCountdownSeconds > 0 || isChallengeExpired"
                      :aria-label="`${$t('myAccount.security.newCode')} ${index + 1}`"
                      @input="onDigitInput('new', $event, index)"
                      @keydown="onDigitKeydown('new', $event, index)"
                      @paste.prevent="onDigitPaste('new', $event, index)"
                    />
                  </div>
                  <small
                    v-if="newInlineMessage"
                    class="my-account-security__countdown"
                  >
                    {{ newInlineMessage }}
                  </small>
                </b-form-group>

                <div class="my-account-security__actions">
                  <b-button
                    variant="outline-secondary"
                    :disabled="loading || verifying"
                    @click="resetFlow"
                  >
                    {{ $t('Cancel') }}
                  </b-button>
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    :disabled="loading || verifying || !isNewCodeComplete || replayCountdownSeconds > 0 || isChallengeExpired"
                    @click="verifyChange"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    <span v-if="verifying">{{ $t('myAccount.security.saving') }}</span>
                    <span v-else>{{ $t('myAccount.security.verifyChange') }}</span>
                  </b-button>
                </div>
              </template>

              <div
                v-else
                class="my-account-security__empty"
              >
                <feather-icon
                  icon="SmartphoneIcon"
                  size="20"
                />
                <span>{{ $t('myAccount.security.waitingForCurrentCode') }}</span>
              </div>
            </section>
          </div>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BFormGroup,
  BOverlay,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import generateQrCodeDataUri from '@core/utils/qrCode'
import useJwt from '@/auth/jwt/useJwt'

export default {
  components: {
    BButton,
    BCard,
    BFormGroup,
    BOverlay,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      currentDigits: ['', '', '', '', '', ''],
      newDigits: ['', '', '', '', '', ''],
      challengeId: '',
      challengeExpiresAt: '',
      challengeExpiresSeconds: 0,
      challengeCountdownTimer: null,
      serverTimeOffsetMs: 0,
      manualKey: '',
      otpauthUri: '',
      loading: false,
      verifying: false,
      error: '',
      replayCountdownSeconds: 0,
      replayCountdownUntil: 0,
      replayCountdownTimer: null,
      errorScope: '',
      countdownScope: '',
    }
  },
  computed: {
    currentCode() {
      return this.currentDigits.join('')
    },
    newCode() {
      return this.newDigits.join('')
    },
    isCurrentCodeComplete() {
      return this.currentCode.length === 6
    },
    isNewCodeComplete() {
      return this.newCode.length === 6
    },
    qrDataUri() {
      if (!this.otpauthUri) return ''
      try {
        return generateQrCodeDataUri(this.otpauthUri)
      } catch (error) {
        return ''
      }
    },
    formattedManualKey() {
      return `${this.manualKey || ''}`.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
    },
    replayCountdownMessage() {
      if (this.replayCountdownSeconds <= 0) return ''
      return this.$t('Please wait for the next code in {seconds} seconds', { seconds: this.replayCountdownSeconds })
    },
    isChallengeExpired() {
      return !!this.challengeId && !!this.challengeExpiresAt && this.challengeExpiresSeconds <= 0
    },
    challengeCountdownMessage() {
      if (!this.challengeId || !this.challengeExpiresAt) return ''
      if (this.isChallengeExpired) return this.$t('myAccount.security.challengeExpired')
      return this.$t('myAccount.security.challengeExpiresIn', { time: this.formatDuration(this.challengeExpiresSeconds) })
    },
    currentInlineMessage() {
      if (this.countdownScope === 'current' && this.replayCountdownMessage) return this.replayCountdownMessage
      if (this.errorScope === 'current') return this.error
      return ''
    },
    newInlineMessage() {
      if (this.countdownScope === 'new' && this.replayCountdownMessage) return this.replayCountdownMessage
      if (this.errorScope === 'new') return this.error
      return ''
    },
  },
  beforeDestroy() {
    this.clearReplayCountdown()
    this.clearChallengeCountdown()
  },
  methods: {
    emptyDigits() {
      return ['', '', '', '', '', '']
    },
    normalizeCode(value) {
      return `${value || ''}`.replace(/\D/g, '').slice(0, 6)
    },
    getDigits(kind) {
      return kind === 'current' ? this.currentDigits : this.newDigits
    },
    setDigits(kind, digits) {
      if (kind === 'current') {
        this.currentDigits = digits
      } else {
        this.newDigits = digits
      }
    },
    getDigitRefs(kind) {
      const refs = this.$refs[kind === 'current' ? 'currentDigit' : 'newDigit']
      if (Array.isArray(refs)) return refs
      return refs ? [refs] : []
    },
    focusDigit(kind, index) {
      this.$nextTick(() => {
        const input = this.getDigitRefs(kind)[index]
        if (!input || !input.focus) return
        input.focus()
        if (input.select) input.select()
      })
    },
    fillDigits(kind, value, startIndex = 0) {
      if (this.replayCountdownSeconds > 0 || (kind === 'new' && this.isChallengeExpired)) return

      const digits = this.normalizeCode(value)
      if (!digits) return

      const next = [...this.getDigits(kind)]
      digits.split('').forEach((digit, offset) => {
        const nextIndex = startIndex + offset
        if (nextIndex < next.length) next[nextIndex] = digit
      })

      this.setDigits(kind, next)
      this.error = ''
      this.errorScope = ''
      this.focusDigit(kind, Math.min(startIndex + digits.length, next.length - 1))
    },
    onDigitInput(kind, event, index) {
      if (this.loading || this.verifying || this.replayCountdownSeconds > 0 || (kind === 'new' && this.isChallengeExpired)) return

      const digits = this.normalizeCode(event.target.value)
      if (digits.length > 1) {
        this.fillDigits(kind, digits, index)
        return
      }

      const next = [...this.getDigits(kind)]
      next[index] = digits
      this.setDigits(kind, next)
      this.error = ''
      this.errorScope = ''

      if (digits && index < next.length - 1) {
        this.focusDigit(kind, index + 1)
      }
    },
    onDigitKeydown(kind, event, index) {
      if (this.loading || this.verifying || this.replayCountdownSeconds > 0 || (kind === 'new' && this.isChallengeExpired)) {
        event.preventDefault()
        return
      }

      if (event.metaKey || event.ctrlKey) return

      if (event.key === 'Enter') {
        event.preventDefault()
        if (kind === 'current') this.beginChange()
        else this.verifyChange()
        return
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.focusDigit(kind, Math.max(index - 1, 0))
        return
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.focusDigit(kind, Math.min(index + 1, this.getDigits(kind).length - 1))
        return
      }

      if (event.key === 'Backspace' && !this.getDigits(kind)[index] && index > 0) {
        event.preventDefault()
        this.focusDigit(kind, index - 1)
        return
      }

      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Home', 'End']
      if (allowedKeys.includes(event.key)) return
      if (/^\d$/.test(event.key)) return

      event.preventDefault()
    },
    onDigitPaste(kind, event, index) {
      if (this.loading || this.verifying || this.replayCountdownSeconds > 0 || (kind === 'new' && this.isChallengeExpired)) return
      const text = event.clipboardData ? event.clipboardData.getData('text') : ''
      this.fillDigits(kind, text, index)
    },
    async beginChange() {
      if (!this.isCurrentCodeComplete || this.loading || this.verifying) return

      this.loading = true
      this.error = ''
      try {
        const response = await useJwt.loginSecurityTotpSelfChangeBegin({
          currentCode: this.currentCode,
        })
        const { data } = response
        this.challengeId = data.challengeId || ''
        this.syncServerClock(data.serverTime)
        this.startChallengeCountdown(data.expiresAt || '')
        this.manualKey = data.manualKey || ''
        this.otpauthUri = data.otpauthUri || ''
        this.currentDigits = this.emptyDigits()
        this.newDigits = this.emptyDigits()
        this.focusDigit('new', 0)
      } catch (error) {
        const message = this.getErrorMessage(error, 'myAccount.security.beginFailed')
        this.error = message
        this.errorScope = 'current'
        this.currentDigits = this.emptyDigits()
        if (this.isReplayError(message)) this.startReplayCountdown('current', this.getRetryAfterSeconds(error))
        else this.focusDigit('current', 0)
      } finally {
        this.loading = false
      }
    },
    async verifyChange() {
      if (!this.challengeId || !this.isNewCodeComplete || this.loading || this.verifying) return
      if (this.isChallengeExpired) {
        this.error = this.$t('myAccount.security.challengeExpired')
        this.errorScope = 'new'
        this.newDigits = this.emptyDigits()
        return
      }

      this.verifying = true
      this.error = ''
      try {
        await useJwt.loginSecurityTotpSelfChangeVerify({
          challengeId: this.challengeId,
          newCode: this.newCode,
        })
        this.showToast('success', this.$t('myAccount.security.changeComplete'), 'CheckCircleIcon')
        this.resetFlow()
      } catch (error) {
        const message = this.getErrorMessage(error, 'myAccount.security.verifyFailed')
        this.error = message
        this.errorScope = 'new'
        this.newDigits = this.emptyDigits()
        if (this.isReplayError(message)) this.startReplayCountdown('new', this.getRetryAfterSeconds(error))
        else this.focusDigit('new', 0)
      } finally {
        this.verifying = false
      }
    },
    resetFlow() {
      this.clearReplayCountdown()
      this.currentDigits = this.emptyDigits()
      this.newDigits = this.emptyDigits()
      this.challengeId = ''
      this.challengeExpiresAt = ''
      this.manualKey = ''
      this.otpauthUri = ''
      this.error = ''
      this.errorScope = ''
      this.countdownScope = ''
      this.clearChallengeCountdown()
    },
    isReplayError(message) {
      return message === 'รหัสนี้เพิ่งถูกใช้แล้ว กรุณารอรหัสชุดใหม่'
        || message === 'This code was just used. Please wait for the next code'
    },
    startReplayCountdown(kind, retryAfterSeconds = 0) {
      this.clearReplayCountdown()
      const stepMs = 30000
      const now = Date.now()
      this.replayCountdownUntil = retryAfterSeconds > 0
        ? now + retryAfterSeconds * 1000
        : (Math.floor(now / stepMs) + 1) * stepMs
      this.countdownScope = kind
      this.updateReplayCountdown(kind)
      this.replayCountdownTimer = window.setInterval(() => this.updateReplayCountdown(kind), 250)
    },
    updateReplayCountdown(kind) {
      const seconds = Math.ceil((this.replayCountdownUntil - Date.now()) / 1000)
      if (seconds <= 0) {
        this.clearReplayCountdown()
        this.focusDigit(kind, 0)
        return
      }
      this.replayCountdownSeconds = seconds
    },
    clearReplayCountdown() {
      if (this.replayCountdownTimer) {
        window.clearInterval(this.replayCountdownTimer)
        this.replayCountdownTimer = null
      }
      this.replayCountdownUntil = 0
      this.replayCountdownSeconds = 0
      this.countdownScope = ''
    },
    syncServerClock(serverTime) {
      const parsed = Date.parse(serverTime)
      this.serverTimeOffsetMs = Number.isNaN(parsed) ? 0 : parsed - Date.now()
    },
    serverNow() {
      return Date.now() + this.serverTimeOffsetMs
    },
    startChallengeCountdown(expiresAt) {
      this.clearChallengeCountdown()
      this.challengeExpiresAt = expiresAt
      this.updateChallengeCountdown()
      if (this.challengeExpiresSeconds > 0) {
        this.challengeCountdownTimer = window.setInterval(this.updateChallengeCountdown, 250)
      }
    },
    updateChallengeCountdown() {
      const expiresAtMs = Date.parse(this.challengeExpiresAt)
      if (Number.isNaN(expiresAtMs)) {
        this.challengeExpiresSeconds = 0
        return
      }

      const seconds = Math.ceil((expiresAtMs - this.serverNow()) / 1000)
      this.challengeExpiresSeconds = Math.max(0, seconds)
      if (this.challengeExpiresSeconds === 0) {
        this.newDigits = this.emptyDigits()
        this.error = this.$t('myAccount.security.challengeExpired')
        this.errorScope = 'new'
        this.clearChallengeCountdown(false)
      }
    },
    clearChallengeCountdown(resetExpires = true) {
      if (this.challengeCountdownTimer) {
        window.clearInterval(this.challengeCountdownTimer)
        this.challengeCountdownTimer = null
      }
      if (resetExpires) {
        this.challengeExpiresAt = ''
        this.challengeExpiresSeconds = 0
      }
    },
    formatDuration(totalSeconds) {
      const seconds = Math.max(0, Number(totalSeconds) || 0)
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${`${remainingSeconds}`.padStart(2, '0')}`
    },
    async copyManualKey() {
      if (!this.manualKey) return
      try {
        await navigator.clipboard.writeText(this.manualKey)
        this.showToast('success', this.$t('myAccount.security.keyCopied'), 'CopyIcon')
      } catch (error) {
        this.error = this.$t('myAccount.security.copyFailed')
      }
    },
    getErrorMessage(error, fallbackKey) {
      const data = error && error.response ? error.response.data : null
      if (typeof data === 'string' && data) return data
      if (data && data.error) {
        if (typeof data.error === 'string') return data.error
        if (data.error.code) return data.error.code
      }
      return this.$t(fallbackKey)
    },
    getRetryAfterSeconds(error) {
      const data = error && error.response ? error.response.data : null
      const value = data ? Number(data.retryAfterSeconds) : 0
      return Number.isFinite(value) && value > 0 ? value : 0
    },
    showToast(variant, text, icon) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t('myAccount.messages.success'),
          icon,
          variant,
          text,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.my-account-security {
  &__panel {
    border: 1px solid rgba(186, 191, 199, 0.35);
    border-radius: 8px;
    padding: 1.5rem;
  }

  &__header,
  &__title,
  &__section-header,
  &__actions,
  &__setup-actions {
    display: flex;
    align-items: center;
  }

  &__header {
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  &__status {
    border-radius: 999px;
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
    font-weight: 600;
    padding: 0.35rem 0.75rem;
    white-space: nowrap;
  }

  &__grid {
    display: grid;
    grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
    gap: 1.25rem;
  }

  &__section {
    border: 1px solid rgba(186, 191, 199, 0.28);
    border-radius: 8px;
    padding: 1.25rem;
    min-height: 100%;
  }

  &__section--muted {
    color: #8b8796;
  }

  &__section-header {
    gap: 0.85rem;
    margin-bottom: 1rem;

    h6 {
      margin-bottom: 0.25rem;
      font-weight: 700;
    }

    p {
      margin-bottom: 0;
      color: #8b8796;
      line-height: 1.45;
    }
  }

  &__step {
    align-items: center;
    background: rgba(234, 84, 85, 0.12);
    border-radius: 50%;
    color: var(--primary);
    display: inline-flex;
    flex: 0 0 2rem;
    font-weight: 700;
    height: 2rem;
    justify-content: center;
    width: 2rem;
  }

  &__otp-inputs {
    display: grid;
    direction: ltr;
    gap: 0.75rem;
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  &__otp-input.form-control {
    border-radius: 8px;
    font-size: 1.35rem;
    font-weight: 700;
    height: 3.25rem;
    line-height: 1;
    padding: 0;
    text-align: center;
  }

  &__setup {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 1rem;
    margin-bottom: 1rem;
  }

  &__qr {
    align-items: center;
    background: #fff;
    border: 1px solid rgba(186, 191, 199, 0.3);
    border-radius: 8px;
    display: flex;
    height: 132px;
    justify-content: center;
    width: 132px;

    img {
      height: 112px;
      width: 112px;
    }
  }

  &__setup-details {
    min-width: 0;

    small {
      color: #8b8796;
      display: block;
      margin-bottom: 0.35rem;
    }

    code {
      background: rgba(186, 191, 199, 0.12);
      border-radius: 6px;
      color: inherit;
      display: block;
      font-size: 0.92rem;
      line-height: 1.5;
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      word-break: break-word;
    }
  }

  &__setup-actions,
  &__actions {
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  &__challenge-countdown {
    color: #8b8796;
    display: block;
    font-weight: 600;
    margin-top: 0.75rem;
  }

  &__challenge-countdown--expired {
    color: var(--danger);
  }

  &__actions {
    justify-content: flex-end;
  }

  &__empty {
    align-items: center;
    border: 1px dashed rgba(186, 191, 199, 0.5);
    border-radius: 8px;
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    min-height: 10rem;
    padding: 1rem;
    text-align: center;
  }

  &__error {
    color: var(--danger);
    display: block;
    font-weight: 600;
    margin-top: 1rem;
  }

  &__countdown {
    color: var(--danger);
    display: block;
    font-weight: 600;
    margin-top: 0.65rem;
  }
}

@media (max-width: 991px) {
  .my-account-security {
    &__grid,
    &__setup {
      grid-template-columns: 1fr;
    }

    &__qr {
      height: 144px;
      width: 144px;
    }
  }
}

@media (max-width: 575px) {
  .my-account-security {
    &__panel {
      padding: 1rem;
    }

    &__header {
      align-items: flex-start;
      flex-direction: column;
    }

    &__actions {
      justify-content: stretch;

      .btn {
        flex: 1 1 auto;
      }
    }

    &__otp-inputs {
      gap: 0.45rem;
    }

    &__otp-input.form-control {
      font-size: 1.15rem;
      height: 2.85rem;
    }
  }
}
</style>
